// ─── addressKey ─────────────────────────────────────────────────────────────
// Canonical form of a street address, for answering "is this the same
// building?" — duplicate detection on deals and on comps both need it.
//
// Lives here rather than inside a component because two callers relying on
// two copies of these tables is how "6399 N 142nd Ave" and
// "6399 North 142nd Avenue" end up as separate records.
//
// Deliberately NOT a geocode. It catches the variants people actually type —
// abbreviated directions, abbreviated street types, stray punctuation — and
// nothing more. Genuinely different spellings of the same street still slip
// through, which is why the deal check also matches on size + price.

const STREET_SUFFIX = {
  ave: "ave", avenue: "ave",
  st: "st", street: "st",
  rd: "rd", road: "rd",
  blvd: "blvd", boulevard: "blvd",
  dr: "dr", drive: "dr",
  ln: "ln", lane: "ln",
  ct: "ct", court: "ct",
  pl: "pl", place: "pl",
  pkwy: "pkwy", parkway: "pkwy",
  hwy: "hwy", highway: "hwy",
  cir: "cir", circle: "cir",
  ter: "ter", terrace: "ter",
  trl: "trl", trail: "trl",
  way: "way",
  sq: "sq", square: "sq",
  expy: "expy", expressway: "expy",
};

const DIRECTIONS = {
  n: "n", north: "n",
  s: "s", south: "s",
  e: "e", east: "e",
  w: "w", west: "w",
  ne: "ne", northeast: "ne",
  nw: "nw", northwest: "nw",
  se: "se", southeast: "se",
  sw: "sw", southwest: "sw",
};

// Common state spellings → postal code, so "Florida" and "FL" agree.
const STATES = {
  alabama: "al", alaska: "ak", arizona: "az", arkansas: "ar", california: "ca",
  colorado: "co", connecticut: "ct", delaware: "de", florida: "fl", georgia: "ga",
  hawaii: "hi", idaho: "id", illinois: "il", indiana: "in", iowa: "ia",
  kansas: "ks", kentucky: "ky", louisiana: "la", maine: "me", maryland: "md",
  massachusetts: "ma", michigan: "mi", minnesota: "mn", mississippi: "ms",
  missouri: "mo", montana: "mt", nebraska: "ne", nevada: "nv",
  "new hampshire": "nh", "new jersey": "nj", "new mexico": "nm", "new york": "ny",
  "north carolina": "nc", "north dakota": "nd", ohio: "oh", oklahoma: "ok",
  oregon: "or", pennsylvania: "pa", "rhode island": "ri", "south carolina": "sc",
  "south dakota": "sd", tennessee: "tn", texas: "tx", utah: "ut", vermont: "vt",
  virginia: "va", washington: "wa", "west virginia": "wv", wisconsin: "wi",
  wyoming: "wy",
};

/** Lowercased, punctuation-stripped, canonical-token form of an address. */
export function normalizeAddress(addr) {
  if (!addr) return "";
  let s = String(addr).toLowerCase().trim();

  // Multi-word state names first, before whitespace collapsing splits them.
  for (const [name, code] of Object.entries(STATES)) {
    if (name.includes(" ")) s = s.replace(new RegExp(`\\b${name}\\b`, "g"), code);
  }

  s = s.replace(/[.,#&]/g, " ").replace(/\s+/g, " ").trim();
  return s.split(" ")
    .map(t => DIRECTIONS[t] || STREET_SUFFIX[t] || STATES[t] || t)
    .join(" ");
}

/**
 * The street-number + street-name portion only — drops the city/state/zip
 * tail so "6399 N 142nd Ave, Clearwater, FL" and "6399 N 142nd Avenue"
 * (entered without a city) still collide. Falls back to the whole
 * normalized string when there's no leading house number to anchor on.
 */
export function addressKey(addr) {
  const norm = normalizeAddress(addr);
  if (!norm) return "";
  const m = norm.match(/^(\d+[a-z]?)\s+(.+)$/);
  if (!m) return norm;
  const [, number, rest] = m;
  // Keep tokens up to and including the street-type suffix; everything after
  // it is locality.
  const tokens = rest.split(" ");
  const suffixSet = new Set(Object.values(STREET_SUFFIX));
  const cut = tokens.findIndex(t => suffixSet.has(t));
  const street = cut >= 0 ? tokens.slice(0, cut + 1) : tokens;
  return `${number} ${street.join(" ")}`.trim();
}

/** Do two addresses describe the same building? */
export function sameAddress(a, b) {
  const ka = addressKey(a), kb = addressKey(b);
  return !!ka && ka === kb;
}
